// Mode-aware range total + label for the trend detail table footer.
// Lives outside the React component so it can be unit-tested in
// isolation -- the combinations of (seriesType, mode) are non-trivial
// and easy to regress.
//
// Rules:
//   - cumulative + any kind: each `point.value` is "value through end
//     of this period", so the last numeric point IS the cumulative
//     total. Don't compound, don't sum.
//   - perPeriod + performance: each value is the period's discrete
//     return; compound to get the range total.
//   - perPeriod + marketValue: summing market values across periods
//     has no meaningful interpretation; suppress the footer.
//   - perPeriod + activity (currency): simple sum (sum of net flows
//     across the range).

function isPerformanceType (seriesType) {
  return typeof seriesType === 'string' && seriesType.includes('performance')
}

function isMarketValueType (seriesType) {
  return typeof seriesType === 'string' && seriesType.includes('market-value')
}

/**
 * @returns {number|null} the footer numeric (null = suppress footer).
 */
export function computeRangeTotal (points, seriesType, mode) {
  if (!Array.isArray(points) || points.length === 0) return null
  const valid = points.filter(p => typeof p?.value === 'number')
  if (valid.length === 0) return null

  if (mode === 'cumulative') {
    return valid[valid.length - 1].value
  }

  // mode === 'perPeriod' (or unknown -> treat as per-period for safety)
  if (isPerformanceType(seriesType)) {
    return valid.reduce((acc, p) => acc * (1 + p.value), 1) - 1
  }
  if (isMarketValueType(seriesType)) {
    return null
  }
  return valid.reduce((acc, p) => acc + p.value, 0)
}

/**
 * Label that appears next to the footer value. Returns null when the
 * footer itself shouldn't render (i.e. computeRangeTotal returns null
 * for that combination by design).
 */
export function rangeTotalLabel (seriesType, mode) {
  if (mode === 'cumulative') {
    if (isPerformanceType(seriesType)) return 'Total Return'
    if (isMarketValueType(seriesType)) return 'Current'
    return 'Cumulative'
  }
  if (isPerformanceType(seriesType)) return 'Compound'
  if (isMarketValueType(seriesType)) return null
  return 'Sum'
}
